<template>
    <div>
    <CDataTable
      :key="tableKey"
      :hover="false"
      :striped="true"
      :bordered="true"
      :items="data.results"
      :fields="processedFields"
      :column-filter="{external: true,lazy:true}"
      :column-filter-value="filters"
      :sorter="{external: true, resetable:true}"
      :loading="loading"
      :sorter-value="sorting"
      @update:column-filter-value="updateFilters"
      @update:sorter-value="updateSorting"
      @row-clicked="onRowClicked"
      :class="customClasses"
    >
      <template #no-items-view>
        <div class="text-center">
          <h5 v-translate>No items found</h5>
        </div>
      </template>
      <template #sorting-icon="{ state, classes }">
        <div :class="classes">
          <CIcon name="cipCaretUp" size="lg" v-if="state === 'asc' || state === 0"></CIcon>
          <CIcon name="cipCaretUp" size="lg" v-else></CIcon>
        </div>
      </template>
      <!-- Celle custom automatiche (boolean, etc.) -->
      <template v-for="field in customCellFields" #[field.slotName]="{ item }">
        <!-- Cella Boolean -->
        <td v-if="field.cellType === 'boolean'" :key="field.key">
          <CIcon v-if="getNestedValue(item, field.dataPath || field.key)" name="cip-check" class="text-success"/>
          <CIcon v-else name="cip-x" class="text-danger"/>
        </td>
      </template>

      <!-- Filtri custom automatici (boolean, dateRange, select) -->
      <template v-for="field in customFilterFields" #[field.filterSlot]>
        <!-- Filtro Boolean -->
        <CSelect
          v-if="field.filterType === 'boolean'"
          :key="field.key + '-bool'"
          size="sm"
          class="mb-0"
          :options="booleanOptions"
          :value.sync="filters[field.key]"
          @update:value="(val) => onBooleanFilterChange(field.key, val)"
        />
        <!-- Filtro Select con options custom -->
        <CSelect
          v-else-if="field.filterType === 'select'"
          :key="field.key + '-select'"
          size="sm"
          class="mb-0"
          :options="field.options"
          :value.sync="filters[field.key]"
          @update:value="(val) => onBooleanFilterChange(field.key, val)"
        />
        <!-- Filtro DateRange -->
        <VueCtkDateTimePicker
          v-else-if="field.filterType === 'dateRange'"
          :key="field.key + '-date'"
          v-model="dateRangeFilters[field.key]"
          :auto-close="false"
          @validate="onDateRangeFilterChange"
          @input="(val) => onDateRangeInput(val, field)"
          range
          :no-button="false"
          :no-shortcuts="false"
          input-size="sm"
          position="bottom"
          :no-label="true"
          :only-date="true"
          format="YYYY-MM-DD"
          formatted="L"
          :right="false"
          :no-header="true"
          :no-button-now="false"
        />
      </template>

      <!-- Pass-through di tutti gli slot dal parent -->
      <template v-for="(slot, name) in $scopedSlots" v-slot:[name]="slotData">
        <slot :name="name" v-bind="slotData"></slot>
      </template>
    </CDataTable>
    <CPagination
      class="mt-3"
      align="center"
      v-show="data.pagination.num_pages > 1"
      :activePage="data.pagination.page"
      @update:activePage="updatePage"
      :pages="data.pagination.num_pages"
    />
  </div>
</template>

<script>
import { saveTableState, loadTableState, clearTableState } from '@/domain/core/utils/tableFilterStorage'

/**
 * AjaxTable - Componente tabella con paginazione, sorting e filtri automatici
 *
 * Props:
 *   - fields: Array di definizioni colonne
 *   - data: Object con { results: [], pagination: { page, num_pages, page_size, total } }
 *   - fetch: Function({ filters, pagination, sorting }) - chiamata per caricare i dati
 *
 * Props per persistenza filtri (tutte opzionali):
 *   - tableName: String - Abilita persistenza se presente (es. 'events', 'collaborators')
 *   - contextKey: String - Chiave contesto opzionale (es. 'picaservice-84590')
 *   - storageMode: String - 'session' (default) | 'persistent' (localStorage con TTL)
 *   - storageTtlHours: Number - TTL per persistent mode (default: 48)
 *   - persistPagination: Boolean - Se salvare anche la pagina corrente
 *
 * Props per sincronizzazione URL (deep-linking):
 *   - syncUrl: Boolean - Abilita sync bidirezionale filtri <-> URL query params
 *   - urlFilterKeys: Array - Chiavi filtri da sincronizzare (default: tutti i filtrabili)
 *
 * Definizione Fields:
 *   {
 *     key: 'column_name',           // Chiave della colonna (obbligatorio)
 *     label: 'Label',               // Etichetta header
 *     sorter: true,                 // Abilita sorting su questa colonna
 *     filter: false,                // Disabilita qualsiasi filtro (default: true)
 *     options: [{label, value}],    // Filtro select con opzioni custom
 *     boolean: true,                // Cella con ✓/✗ + filtro select Sì/No/Qualsiasi (se filter !== false)
 *     dataPath: 'nested.path',      // Path per valore nested (es. 'event.is_draft')
 *     dateRangeFrom: 'param_from',  // Filtro date range - parametro "da"
 *     dateRangeTo: 'param_to',      // Filtro date range - parametro "a"
 *     defaultValue: 'value',        // Valore di default per il filtro
 *     _style: 'width:100px',        // Stile inline colonna
 *     _classes: 'sticky-column',    // Classi CSS colonna
 *   }
 *
 * Stili automatici:
 *   - Colonna con key 'id': larghezza fissa 70px (sovrascrivibile con _style)
 *
 * Celle custom automatiche (customCellFields):
 *   - boolean: true → Mostra ✓/✗ (sovrascrivibile con slot custom)
 *
 * Filtri custom automatici (customFilterFields) - rispettano filter: false:
 *   - boolean: true → Select con Qualsiasi/Sì/No
 *   - options: [...] → Select con opzioni custom
 *   - dateRangeFrom/dateRangeTo → Date picker con range
 *
 * Esempi:
 *   // Colonna con sorting
 *   {key: 'name', label: 'Nome', sorter: true}
 *
 *   // Colonna boolean (filtro select + cella ✓/✗)
 *   {key: 'is_active', label: 'Attivo', boolean: true, sorter: true}
 *
 *   // Colonna boolean senza filtro (solo cella ✓/✗)
 *   {key: 'is_promo', label: 'Promo', boolean: true, filter: false, sorter: true}
 *
 *   // Colonna con filtro select custom
 *   {key: 'status', label: 'Stato', options: [{label: 'Attivo', value: '1'}]}
 *
 *   // Colonna con filtro date range
 *   {key: 'created_at', label: 'Data', dateRangeFrom: 'date_from', dateRangeTo: 'date_to', sorter: true}
 *
 * Metodi esposti via ref:
 *   - fetchList(): Ricarica i dati
 *   - hasActiveFilters(): Ritorna true se ci sono filtri attivi
 *   - resetFilters(): Resetta filtri e cancella storage
 *   - setFilters(filters, merge): Setta filtri dall'esterno (per componenti filtri custom)
 *   - getFilters(): Ritorna i filtri correnti
 */
export default {
  name: "AjaxTable",
  props: {
    fields: {type: Array},
    data: {type: Object},
    fetch: {type: Function},
    // Persistence props
    tableName: {type: String, default: null},
    contextKey: {type: String, default: null},
    storageMode: {type: String, default: 'session'},
    storageTtlHours: {type: Number, default: 48},
    persistPagination: {type: Boolean, default: false},
    // URL sync props
    syncUrl: {type: Boolean, default: false},
    urlFilterKeys: {type: Array, default: null},
    customClasses: {type: String, default: 'custom-table custom-table--has-filters custom-table--clean'},
  },
  emits: ['filters-changed', 'row-clicked'],
  data() {
    return {
      pagination: {
        page_size: 20,
        page: 1,
      },
      sorting: {
        column: null,
        asc: false
      },
      filters: {},
      dateRangeFilters: {},
      loading: true,
    }
  },
  computed: {
    customFilterFields() {
      // Tutti i fields con filtri custom (boolean, select, dateRange)
      // Rispetta filter: false per non mostrare il filtro
      // Ogni field ha: filterSlot, filterType
      if (!this.fields) return []
      const fields = []

      this.fields.forEach(field => {
        // Se filter è esplicitamente false, non mostrare filtro
        if (field.filter === false) return

        if (field.boolean === true) {
          fields.push({...field, filterSlot: field.key + '-filter', filterType: 'boolean'})
        } else if (field.options && Array.isArray(field.options)) {
          fields.push({...field, filterSlot: field.key + '-filter', filterType: 'select'})
        } else if (field.dateRangeFrom || field.dateRangeTo) {
          fields.push({...field, filterSlot: field.key + '-filter', filterType: 'dateRange'})
        }
      })

      return fields
    },
    customCellFields() {
      // Fields con celle custom (boolean, etc.) che non hanno slot sovrascritto dal parent
      // Ogni field ha: slotName, cellType
      if (!this.fields) return []
      const fields = []

      this.fields.forEach(field => {
        // Skip se il parent ha già definito uno slot per questa colonna
        if (this.$scopedSlots[field.key]) return

        if (field.boolean === true) {
          fields.push({...field, slotName: field.key, cellType: 'boolean'})
        }
        // Qui si possono aggiungere altri tipi di celle custom in futuro
      })

      return fields
    },
    booleanOptions() {
      return [
        {value: null, label: this.$gettext('Any')},
        {value: 'true', label: this.$gettext('Yes')},
        {value: 'false', label: this.$gettext('No')},
      ]
    },
    tableKey() {
      // Forza re-render quando cambiano i fields
      return this.fields ? this.fields.length : 0
    },
    processedFields() {
      // Applica stili automatici ai fields (es. larghezza fissa per colonna 'id')
      if (!this.fields) return []
      return this.fields.map(field => {
        if (field.key === 'id' && !field._style) {
          return { ...field, _style: 'width: 70px' }
        }
        return field
      })
    },
    fieldKeys() {
      // Estrae le chiavi dei fields per validazione storage
      if (!this.fields) return []
      return this.fields.map(f => f.key)
    },
    storageOptions() {
      return {
        mode: this.storageMode,
        ttlHours: this.storageTtlHours,
        fieldKeys: this.fieldKeys
      }
    },
    // Merge standard filters with date range filters for the fetch call
    // Rimuove valori vuoti (null, undefined, '') così il consumatore non deve gestirli
    mergedFilters() {
      const merged = {}

      // Copia solo i filtri con valori significativi
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          merged[key] = value
        }
      })

      // Add date range filters
      const dateRangeFields = this.customFilterFields.filter(f => f.filterType === 'dateRange')
      for (const field of dateRangeFields) {
        const dateRange = this.dateRangeFilters[field.key]
        if (dateRange && dateRange.start) {
          merged[field.dateRangeFrom || `${field.key}_from`] = dateRange.start
        }
        if (dateRange && dateRange.end) {
          merged[field.dateRangeTo || `${field.key}_to`] = dateRange.end
        }
      }
      return merged
    },
  },
  watch: {
    contextKey() {
      // Quando cambia il contesto (es. cambio contratto), resetta e ricarica
      this.filters = {}
      this.dateRangeFilters = {}
      this.sorting = { column: null, asc: false }
      this.pagination = { page_size: 20, page: 1 }
      this.initializeDefaultFilters()
      this.loadSavedState()
      if (this.syncUrl) {
        this.loadFiltersFromUrl()
      }
      this.emitFiltersChanged()
      this.fetchList()
    },
    '$route.query': {
      handler(newQuery, oldQuery) {
        // Reagisce a cambiamenti esterni dell'URL (es. back/forward browser)
        if (!this.syncUrl) return
        if (JSON.stringify(newQuery) !== JSON.stringify(oldQuery)) {
          this.loadFiltersFromUrl()
          this.emitFiltersChanged()
          this.fetchList()
        }
      }
    }
  },
  mounted() {
    this.initializeDefaultFilters()
    this.loadSavedState()
    // URL filters hanno priorità su storage (permettono deep-linking)
    if (this.syncUrl) {
      this.loadFiltersFromUrl()
    }
    this.emitFiltersChanged()
    this.fetchList()
  },
  methods: {
    initializeDefaultFilters() {
      // Legge i valori di default dai fields con defaultValue
      if (!this.fields) return

      const defaultFilters = {}
      const dateRangeKeys = new Set(
        this.customFilterFields.filter(f => f.filterType === 'dateRange').map(f => f.key)
      )

      this.fields.forEach(field => {
        if (field.defaultValue !== undefined) {
          if (dateRangeKeys.has(field.key)) {
            if (!this.dateRangeFilters[field.key]) {
              this.$set(this.dateRangeFilters, field.key, field.defaultValue)
            }
          } else {
            defaultFilters[field.key] = field.defaultValue
          }
        }
      })

      this.filters = {...defaultFilters, ...this.filters}
    },
    loadSavedState() {
      // Carica stato salvato se tableName è definito
      if (!this.tableName) return

      const savedState = loadTableState(this.tableName, this.contextKey, this.storageOptions)
      if (!savedState) return

      // Applica filtri salvati (mergiati con defaultFilters già applicati)
      if (savedState.filters && Object.keys(savedState.filters).length > 0) {
        this.filters = { ...this.filters, ...savedState.filters }
      }

      // Applica date range filters salvati
      if (savedState.dateRangeFilters && Object.keys(savedState.dateRangeFilters).length > 0) {
        this.dateRangeFilters = { ...this.dateRangeFilters, ...savedState.dateRangeFilters }
      }

      // Applica sorting salvato
      if (savedState.sorting && savedState.sorting.column) {
        this.sorting = savedState.sorting
      }

      // Applica paginazione salvata solo se persistPagination è abilitato
      if (this.persistPagination && savedState.pagination) {
        this.pagination = { ...this.pagination, ...savedState.pagination }
      }
    },
    /**
     * Ritorna le chiavi dei filtri da sincronizzare con URL
     * Se urlFilterKeys è specificato usa quello, altrimenti usa tutti i field keys filtrabili
     */
    getUrlFilterKeys() {
      if (this.urlFilterKeys) return this.urlFilterKeys
      if (!this.fields) return []
      return this.fields
        .filter(f => f.filter !== false)
        .map(f => f.key)
    },
    /**
     * Carica i filtri dall'URL query params
     */
    loadFiltersFromUrl() {
      if (!this.syncUrl) return

      const keys = this.getUrlFilterKeys()
      const query = this.$route.query

      keys.forEach(key => {
        if (query[key] !== undefined && query[key] !== '') {
          this.$set(this.filters, key, query[key])
        }
      })

      // Gestisce anche date range filters (cerca pattern _from/_to)
      const dateRangeFields = this.customFilterFields.filter(f => f.filterType === 'dateRange')
      dateRangeFields.forEach(field => {
        const fromKey = field.dateRangeFrom || `${field.key}_from`
        const toKey = field.dateRangeTo || `${field.key}_to`

        if (query[fromKey] || query[toKey]) {
          this.$set(this.dateRangeFilters, field.key, {
            start: query[fromKey] || null,
            end: query[toKey] || null
          })
        }
      })
    },
    /**
     * Aggiorna l'URL con i filtri correnti
     */
    updateUrlFromFilters() {
      if (!this.syncUrl) return

      const keys = this.getUrlFilterKeys()
      const query = { ...this.$route.query }

      // Aggiorna filtri standard
      keys.forEach(key => {
        const value = this.filters[key]
        if (value !== null && value !== undefined && value !== '') {
          query[key] = String(value)
        } else {
          delete query[key]
        }
      })

      // Aggiorna date range filters
      const dateRangeFields = this.customFilterFields.filter(f => f.filterType === 'dateRange')
      dateRangeFields.forEach(field => {
        const fromKey = field.dateRangeFrom || `${field.key}_from`
        const toKey = field.dateRangeTo || `${field.key}_to`
        const dateRange = this.dateRangeFilters[field.key]

        if (dateRange && dateRange.start) {
          query[fromKey] = dateRange.start
        } else {
          delete query[fromKey]
        }

        if (dateRange && dateRange.end) {
          query[toKey] = dateRange.end
        } else {
          delete query[toKey]
        }
      })

      // Evita navigazione se query è uguale
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ query }).catch(() => {})
      }
    },
    saveState() {
      // Salva stato se tableName è definito
      if (this.tableName) {
        const stateToSave = {
          filters: this.filters,
          dateRangeFilters: this.dateRangeFilters,
          sorting: this.sorting
        }

        // Salva paginazione solo se persistPagination è abilitato
        if (this.persistPagination) {
          stateToSave.pagination = this.pagination
        }

        saveTableState(this.tableName, this.contextKey, stateToSave, this.storageOptions)
      }

      // Aggiorna URL se syncUrl è abilitato
      if (this.syncUrl) {
        this.updateUrlFromFilters()
      }
    },
    resetFilters() {
      // Resetta filtri e cancella storage
      this.filters = {}
      this.dateRangeFilters = {}
      this.sorting = { column: null, asc: false }
      this.pagination = { page_size: 20, page: 1 }

      // Re-inizializza i defaultFilters
      this.initializeDefaultFilters()

      // Cancella storage
      if (this.tableName) {
        clearTableState(this.tableName, this.contextKey, this.storageMode)
      }

      // Pulisce URL
      if (this.syncUrl) {
        this.updateUrlFromFilters()
      }

      this.emitFiltersChanged()
      this.fetchList()
    },
    async fetchList() {
      this.loading = true
      await this.fetch({
        filters: this.mergedFilters,
        pagination: this.pagination,
        sorting: this.sorting
      })
      this.pagination.page = this.data.pagination.page
      this.loading = false
    },
    updateFilters(filters) {
      this.filters = filters
      this.pagination.page = 1
      this.saveState()
      this.emitFiltersChanged()
      this.fetchList()
    },
    updateSorting(sorting) {
      this.sorting = sorting
      this.saveState()
      this.fetchList()
    },
    updatePage(page) {
      this.pagination.page = page
      this.saveState()
      this.fetchList()
    },
    onBooleanFilterChange(key, val) {
      this.$set(this.filters, key, val)
      this.pagination.page = 1
      this.saveState()
      this.emitFiltersChanged()
      this.fetchList()
    },
    onDateRangeFilterChange() {
      // Quando viene confermato un filtro dateRange, aggiorna la lista
      this.pagination.page = 1
      this.saveState()
      this.emitFiltersChanged()
      this.fetchList()
    },
    onDateRangeInput(value, field) {
      // Quando viene cancellato il valore del date range
      if (!value) {
        this.$set(this.dateRangeFilters, field.key, {})
        this.pagination.page = 1
        this.saveState()
        this.emitFiltersChanged()
        this.fetchList()
      }
    },
    hasActiveFilters() {
      // Controlla se ci sono filtri attivi (select, text o date range)
      const hasTextOrSelectFilters = Object.values(this.filters).some(v => v !== '' && v !== null && v !== undefined)
      const hasDateRangeFilters = Object.values(this.dateRangeFilters).some(v => v && (v.start || v.end))
      return hasTextOrSelectFilters || hasDateRangeFilters
    },
    /**
     * Setta filtri dall'esterno (es. da un componente filtri custom)
     * @param {Object} filters - Filtri da applicare
     * @param {Boolean} merge - Se true, mergia con filtri esistenti (default: false = replace)
     */
    setFilters(filters, merge = false) {
      if (merge) {
        this.filters = { ...this.filters, ...filters }
      } else {
        this.filters = { ...filters }
      }
      this.pagination.page = 1
      this.saveState()
      this.emitFiltersChanged()
      this.fetchList()
    },
    /**
     * Ritorna i filtri correnti (utile per componenti filtri esterni)
     */
    getFilters() {
      return { ...this.mergedFilters }
    },
    emitFiltersChanged() {
      this.$emit('filters-changed', this.hasActiveFilters())
    },
    onRowClicked(item, index, columnName, event) {
      this.$emit('row-clicked', item, index, columnName, event)
    },
    getNestedValue(obj, path) {
      // Accede a valori nested es. 'event.is_draft' → obj.event.is_draft
      return path.split('.').reduce((o, k) => (o || {})[k], obj)
    }
  }
}
</script>